/**
 * What the console *says* — and nothing about what anything is.
 *
 * The module catalogue (spec §3.1) and the scope vocabulary (spec §3.3) used to
 * be copied into this file, alongside `apps/cli` and `apps/admin-api`. They have
 * been promoted to `packages/domain` (`modules.ts`, next to `capabilitiesFor`),
 * so the three surfaces now read one table and the screens import `MODULES`,
 * `SCOPES`, `scopeSpec` and `CONCIERGE_SCOPE_LIST` straight from
 * `@yourstore/domain`.
 *
 * What is left here is genuinely the console's own: the shortest true path
 * through the wizard, and the two sentences a lifecycle move is quoted from.
 * Those are presentation — how a consequence is worded to the person about to
 * cause it — and they belong next to the screen that shows them rather than in
 * the vocabulary every service shares.
 *
 * This file still decides nothing. Every verb belongs to the server, which stays
 * the authority on every refusal the console renders.
 */

/**
 * The modules the partner path needs, in dependency order.
 *
 * `partner_api` depends on `calendar`, which depends on `clients` and
 * `services`. The wizard offers exactly this set pre-ticked because it is the
 * shortest true path to "taking partner bookings" (plan, MVP 5 acceptance) —
 * and because the server validates dependencies against the state a request
 * would *leave behind*, all four can go in one call.
 */
export const PARTNER_PATH_MODULES: readonly string[] = [
  'clients',
  'services',
  'calendar',
  'partner_api',
];

/** Lifecycle vocabulary (spec §5.7.1, §4.4) — what each status means, said plainly. */
export const STATUS_MEANING: Record<string, string> = {
  onboarding: 'Being set up. The partner is not connected yet.',
  importing: 'Dual-running with the old system during cutover — availability reports approximate.',
  live: 'We hold the book. This is the only status that reports authority: "live".',
  paused: 'Temporarily not answering: capabilities degrade to availability none, reserve none.',
  offboarded: 'Finished with us. Nothing is bookable and clients report none.',
};

/**
 * What flipping to this status *costs*, in the sentence a confirm dialog quotes.
 *
 * Every one of these is a consequence the agent on the other end of the
 * agreement will observe within seconds, which is why the console refuses to
 * make a lifecycle move a single unconfirmed click.
 */
export const STATUS_CONSEQUENCE: Record<string, string> = {
  onboarding:
    'The tenant stops reporting authority "live". An agent mid-conversation will begin hedging on every time it quotes.',
  importing:
    'Availability reports "approximate" — the agent hedges, because another system can still take a booking during cutover (spec §4.4).',
  live: 'This is the cutover. From this moment we hold the book: the agent is told authority "live" and will promise times to guests on the strength of it.',
  paused:
    'Capabilities degrade to availability "none" and reserve "none". The agent routes around this tenant rather than failing mid-conversation.',
  offboarded:
    'The tenant is finished with us. Nothing is bookable, clients report "none", and the id is never reused.',
};
